import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { LayoutChangeEvent, View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { useCountdownTimer } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/useCountdownTimer'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { PrivacyIllustration } from '../../atoms/illustrations/PrivacyIllustration'
import { TimeoutIllustration } from '../../atoms/illustrations/TimeoutIllustration'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { TertiaryButton } from '../../atoms/tertiaryButton/TertiaryButton'
import { Title } from '../../atoms/title/Title'
import { IS_WEB } from '../../constants'
import { TitleSize } from '../../styles/typeStyles'
import { Flex1View } from '../../templates/content/CommonViews'
import { tID } from '../../utils/utils'

export interface InactivityTimeoutProps {
  warningDuration?: number
  onPressResume: () => void
  onPressSignOut: () => void
  onPressSignIn: () => void
  onWarningTimeUp: () => void
  onHeightChange?: (height: number) => void
  inactivityState?: INACTIVITY_STATE
  isWeb?: boolean
}

export enum INACTIVITY_STATE {
  WARNING = 'warning',
  SIGNED_OUT = 'signed_out',
}

interface InactivityContent {
  header: JSX.Element
  bodyText: JSX.Element
  buttons: { text: JSX.Element; onPress: () => void; other?: JSX.Element }
  image: JSX.Element
  imagePaddingTop?: number
  imagePaddingBottom?: number
}

const InactivityTimeoutContainer = styled(View)({
  display: 'flex',
  flexDirection: 'column-reverse',
  justifyContent: 'center',
  ...(IS_WEB && { marginBottom: '-32px' }),
  paddingTop: '20px',
})

const ButtonsContainer = styled.View(({ theme }) => ({
  alignSelf: 'flex-end',
  width: theme.breakpoints.isMobileSized ? '100%' : 'auto',
  marginTop: theme.spacing['32px'],
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['8px'] : 0,
  flexDirection: theme.breakpoints.isMobileSized ? 'column-reverse' : 'row',
  gap: theme.breakpoints.isMobileSized ? theme.spacing['8px'] : theme.spacing['16px'],
}))

const ImageContainer = styled.View<{ paddingTop?: number; paddingBottom?: number }>(
  ({ theme, paddingTop = 0, paddingBottom = 0 }) => ({
    alignItems: 'center',
    paddingTop: `${paddingTop}px`,
    paddingBottom: `${paddingBottom}px`,
    alignSelf: 'center',
    ...(!theme.breakpoints.isMinWidthTablet && { flexDirection: 'row', flexBasis: 'auto' }),
  }),
)

const TitleStyled = styled(Title)(({ theme }) => ({
  marginBottom: theme.spacing['16px'],
}))

export const InactivityTimeout: FunctionComponent<InactivityTimeoutProps> = ({
  warningDuration = 60,
  onPressResume,
  onPressSignOut,
  onPressSignIn,
  onWarningTimeUp,
  onHeightChange,
  inactivityState = INACTIVITY_STATE.WARNING,
  isWeb = true,
}) => {
  const [loggedOutState, setLoggedOutState] = useState(inactivityState)
  const [countdown] = useCountdownTimer(warningDuration, isWeb)
  const hasCalledWarningTimeUpRef = useRef(false)

  useEffect(() => {
    hasCalledWarningTimeUpRef.current = false
  }, [warningDuration])

  const { breakpoints, colors } = useTheme()
  const content: { [key in INACTIVITY_STATE]: InactivityContent } = {
    [INACTIVITY_STATE.WARNING]: {
      header: (
        <FormattedMessage
          defaultMessage='Are you still there?'
          description='Header text asking if the user is still active'
        />
      ),
      bodyText: (
        <FormattedMessage
          defaultMessage='We noticed you haven’t been active in a while. To protect your privacy we will sign you out in <bold>{countdown} {countdown, plural, =1 {second} other {seconds}}</bold>'
          description='Text warning user about how much time is left before they are logged out'
          values={{
            bold: (chunks: any) => {
              return <BodyText dataSet={{ chromatic: 'ignore' }} text={chunks} bold />
            },
            countdown: Math.floor(countdown),
          }}
        />
      ),
      buttons: {
        text: <FormattedMessage defaultMessage='Resume' description='Button text to stay logged in' />,
        onPress: onPressResume,
        other: (
          <TertiaryButton
            text={<FormattedMessage defaultMessage='Sign out' description='Button text to sign out of the account' />}
            onPress={onPressSignOut}
            testID={tID('InactivityTimeout-secondaryButton')}
            accessibilityLabel={'Sign out'}
            fullWidth={breakpoints.isMobileSized}
            customTextColor={colors.textActive}
          />
        ),
      },
      image: <TimeoutIllustration />,
      imagePaddingBottom: 16,
    },
    [INACTIVITY_STATE.SIGNED_OUT]: {
      header: (
        <FormattedMessage
          defaultMessage='To protect your privacy, we’ve logged you out. '
          description='Header text stating user has been logged out'
        />
      ),
      bodyText: (
        <FormattedMessage
          defaultMessage='We’re always here so rejoin us when you’re ready.'
          description='Text telling user to come back to the app when ready'
        />
      ),
      buttons: {
        text: <FormattedMessage defaultMessage='Sign In' description='Button text to sign in' />,
        onPress: onPressSignIn,
      },
      image: <PrivacyIllustration />,
      imagePaddingBottom: 16,
    },
  }
  const activeContent = content[loggedOutState]

  const handleLayoutChange = (event: LayoutChangeEvent) => {
    if (onHeightChange) {
      onHeightChange(event.nativeEvent.layout.height)
    }
  }

  useEffect(() => {
    // Only trigger when countdown reaches 0 for the first time in WARNING state
    if (countdown === 0 && loggedOutState === INACTIVITY_STATE.WARNING && !hasCalledWarningTimeUpRef.current) {
      onWarningTimeUp()
      hasCalledWarningTimeUpRef.current = true
      setLoggedOutState(INACTIVITY_STATE.SIGNED_OUT)
    }
  }, [countdown, loggedOutState, onWarningTimeUp])

  return (
    <InactivityTimeoutContainer
      nativeID='InactivityTimeout'
      testID={tID('InactivityTimeout')}
      onLayout={handleLayoutChange}
    >
      <Flex1View>
        <Flex1View testID={tID('InactivityTimeout-body')}>
          <TitleStyled text={activeContent.header} size={TitleSize.MEDIUM} textAlign={'center'} />
          <BodyText text={activeContent.bodyText} textAlign={'center'} />
        </Flex1View>
        <ButtonsContainer>
          {activeContent.buttons.other}
          <PrimaryButton
            text={activeContent.buttons.text}
            onPress={activeContent.buttons.onPress}
            testID={tID('InactivityTimeout-primaryButton')}
            fullWidth={breakpoints.isMobileSized}
          />
        </ButtonsContainer>
      </Flex1View>
      <ImageContainer paddingTop={activeContent.imagePaddingTop} paddingBottom={activeContent.imagePaddingBottom}>
        {activeContent.image}
      </ImageContainer>
    </InactivityTimeoutContainer>
  )
}
